import type { FormField } from '../template/types'
import { el } from './dom'

/**
 * Builds the inputs for a set of template fields and reads them back.
 *
 * The fields are declared by the template rather than by markup, so this is the
 * one place that turns a FormField into something you can type into.
 */
type Control = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export class FieldForm {
  private inputs = new Map<string, Control>()
  private errors = new Map<string, HTMLElement>()
  /** Free-text boxes belonging to choice fields that allow one. */
  private otherInputs = new Map<string, HTMLTextAreaElement>()

  constructor(
    private readonly fields: FormField[],
    private readonly values: Record<string, string>,
    private readonly onChanged: () => void = () => {},
  ) {}

  render(): HTMLElement {
    const container = el('div', { class: 'fields' })

    for (const field of this.fields) {
      if (field.type === 'choice' && field.allowOther) {
        container.append(this.choiceWithOther(field))
        continue
      }

      const id = `field-${field.key}`
      const input = this.controlFor(field, id)

      input.value = this.values[field.key] ?? ''
      if (field.required) input.setAttribute('aria-required', 'true')

      const error = el('p', { class: 'field-error', id: `${id}-error`, role: 'alert' })
      error.hidden = true

      const record = () => {
        this.values[field.key] = input.value
        if (input.value.trim()) this.hideError(field.key)
        this.onChanged()
      }
      // Both events: a <select> is 'change' territory, and not every browser
      // fires 'input' on one.
      input.addEventListener('input', record)
      input.addEventListener('change', record)

      container.append(
        el('div', { class: 'field' }, [
          el('label', { class: 'field-label', for: id }, [
            field.label,
            field.required ? el('span', { class: 'required', text: ' *', 'aria-label': ' required' }) : null,
          ]),
          field.hint ? el('p', { class: 'field-hint', text: field.hint }) : null,
          input,
          error,
        ]),
      )

      this.inputs.set(field.key, input)
      this.errors.set(field.key, error)
    }

    return container
  }

  /** Marks empty required fields and returns their labels. */
  validate(): string[] {
    const missing: string[] = []
    for (const field of this.fields) {
      if (field.required && !(this.values[field.key] ?? '').trim()) {
        this.showError(field.key, 'Required')
        missing.push(field.label)
      } else {
        this.hideError(field.key)
      }
    }
    return missing
  }

  focusFirstMissing(): void {
    const first = this.fields.find((f) => f.required && !(this.values[f.key] ?? '').trim())
    if (!first) return
    // If the escape hatch is showing and empty, that is the gap, not the picker.
    const other = this.otherInputs.get(first.key)
    if (other && !other.hidden) other.focus()
    else this.inputs.get(first.key)?.focus()
  }

  /**
   * A picker with a free-text escape hatch.
   *
   * The stored value is always the sentence that will be printed: either the
   * option picked, or whatever was typed. The word "Something else" is a UI
   * state and must never reach the PDF.
   */
  private choiceWithOther(field: FormField): HTMLElement {
    const id = `field-${field.key}`
    const OTHER = ' other'

    const select = el('select', { id, class: 'input select' })
    select.append(el('option', { value: '', text: 'Choose one…' }))
    for (const option of field.options) {
      select.append(el('option', { value: option, text: option }))
    }
    select.append(el('option', { value: OTHER, text: `${field.otherLabel}…` }))

    const other = el('textarea', {
      class: 'input other-input',
      rows: 2,
      placeholder: 'Describe it in a sentence',
      // Deliberately not prefixed with the field label: an accessible name that
      // starts the same way as the picker's makes the two indistinguishable to
      // anything selecting by label, screen readers and tests alike.
      'aria-label': `Describe ${field.label.toLowerCase()}`,
    })

    const error = el('p', { class: 'field-error', id: `${id}-error`, role: 'alert' })
    error.hidden = true

    // An existing value that is not one of the options can only have come from
    // the escape hatch, so restore it there.
    const existing = this.values[field.key] ?? ''
    const isOther = existing !== '' && !field.options.includes(existing)
    select.value = isOther ? OTHER : existing
    other.value = isOther ? existing : ''
    other.hidden = !isOther

    const sync = () => {
      const picked = select.value === OTHER
      other.hidden = !picked
      this.values[field.key] = picked ? other.value : select.value
      if ((this.values[field.key] ?? '').trim()) this.hideError(field.key)
      this.onChanged()
    }
    select.addEventListener('change', sync)
    other.addEventListener('input', sync)

    this.inputs.set(field.key, select)
    this.errors.set(field.key, error)
    this.otherInputs.set(field.key, other)

    return el('div', { class: 'field' }, [
      el('label', { class: 'field-label', for: id }, [
        field.label,
        field.required ? el('span', { class: 'required', text: ' *', 'aria-label': ' required' }) : null,
      ]),
      field.hint ? el('p', { class: 'field-hint', text: field.hint }) : null,
      select,
      other,
      error,
    ])
  }

  private controlFor(field: FormField, id: string): Control {
    if (field.type === 'choice') {
      const select = el('select', { id, class: 'input select' })
      // A blank first entry so a required choice is a deliberate pick rather
      // than whatever happened to be at the top of the list.
      select.append(el('option', { value: '', text: 'Choose one…' }))
      for (const option of field.options) {
        select.append(el('option', { value: option, text: option }))
      }
      return select
    }
    if (field.type === 'multiline') {
      return el('textarea', { id, class: 'input', rows: 3 })
    }
    return el('input', { id, class: 'input', type: inputTypeFor(field), inputmode: inputModeFor(field) })
  }

  private showError(key: string, message: string): void {
    const error = this.errors.get(key)
    const input = this.inputs.get(key)
    if (!error || !input) return
    error.textContent = message
    error.hidden = false
    input.classList.add('invalid')
    input.setAttribute('aria-invalid', 'true')
    input.setAttribute('aria-describedby', `field-${key}-error`)
  }

  private hideError(key: string): void {
    const error = this.errors.get(key)
    const input = this.inputs.get(key)
    if (!error || !input) return
    error.hidden = true
    input.classList.remove('invalid')
    input.removeAttribute('aria-invalid')
    input.removeAttribute('aria-describedby')
  }
}

function inputTypeFor(field: FormField): string {
  switch (field.type) {
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    case 'date':
      return 'date'
    default:
      return 'text'
  }
}

function inputModeFor(field: FormField): string {
  switch (field.type) {
    case 'email':
      return 'email'
    case 'phone':
      return 'tel'
    default:
      return 'text'
  }
}
